import { useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, FolderX, Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MatrixGrid } from '@/components/ui/matrix-grid';
import { EmptyState } from '@/components/ui/empty-state';
import { toneClass } from '@/lib/status';
import { buildCoverage, coverageSubLine, plural } from './documentsModel';
import { Segmented, edgeFadeStyle, useScrollEdges } from './ui';

const COLLAPSED_ROWS = 6;
const SHORT = { 'Handing over documents': 'Handing over', 'Technical Submittal': 'Technical Submittal' };

function LegendPill({ tone, children }) {
  return (
    <span className={cn('inline-flex h-5 min-w-[22px] items-center justify-center rounded-[5px] px-1.5 text-[11px] font-bold', toneClass(tone), 'bg-[color:var(--tone-bg)] text-[color:var(--tone-ink)]')}>
      {children}
    </span>
  );
}

/**
 * Coverage by stage (16 idea 9): open projects × the 10 delivery stages — how many documents are filed for each
 * stage, and for a stage without documents what its OWN status says: "Due" while it is being worked on (In Progress,
 * Submitted, Rejected — or the Not Started stage the project is at), "Missing" once it is Approved / Completed, a
 * grey dot while Not Started. A count opens those documents in the register; Due / Missing opens Upload with the
 * project and stage filled in. Built from the register and the project list on the page.
 */
export default function CoverageCard({
  docs, projects, projectsLoading, projectsError, canSeeLpoLoi, canEdit, onShowDocuments, onUpload, onShowUnlinked,
}) {
  const [scope, setScope] = useState('open');
  const [expanded, setExpanded] = useState(false);
  const coverage = useMemo(
    () => buildCoverage({ docs, projects: projects || [], canSeeLpoLoi, scope }),
    [docs, projects, canSeeLpoLoi, scope]
  );
  const shownRows = expanded ? coverage.rows : coverage.rows.slice(0, COLLAPSED_ROWS);
  const hidden = coverage.rows.length - shownRows.length;
  const byKey = useMemo(() => new Map(coverage.rows.map((row) => [row.project.id, row])), [coverage.rows]);

  // The project name opens the project (MatrixGrid's row link: navy, like every project link).
  const rows = shownRows.map((row) => ({
    key: row.project.id,
    name: row.project.name,
    label: row.project.name,
    href: `/projects/${row.project.id}`,
    linkTitle: `Open ${row.project.name}`,
    linkTestId: `coverage-project-${row.project.id}`,
    sublabel: coverageSubLine(row.project),
  }));
  const columns = coverage.columns.map((name) => ({ key: name, label: SHORT[name] || name, title: name }));

  const getCell = (row, column) => {
    const cell = byKey.get(row.key)?.cells[column.key];
    const where = `${row.name} · ${column.title}`;
    switch (cell?.kind) {
      case 'count':
        return { tone: 'navy', value: cell.count, title: `${where}: ${plural(cell.count, 'document')} filed — show them` };
      case 'due':
        return { tone: 'amber', label: 'Due', title: `${where}: ${cell.status || 'current stage'}, nothing filed${canEdit ? ' — upload one' : ''}`, disabled: !canEdit };
      case 'missing':
        return { tone: 'red', label: 'Missing', title: `${where}: ${cell.status || 'stage done'}, no document${canEdit ? ' — upload one' : ''}`, disabled: !canEdit };
      case 'skip':
        return { empty: true, title: `${where}: not required`, disabled: true };
      case 'none':
        return { empty: true, title: `${where}: not a stage of this project`, disabled: true };
      default:
        return { empty: true, title: `${where}: not started yet`, disabled: true };
    }
  };

  const onCellClick = (row, column, cell) => {
    const kind = byKey.get(row.key)?.cells[column.key]?.kind;
    if (kind === 'count') onShowDocuments(row.key, column.key);
    else if ((kind === 'due' || kind === 'missing') && canEdit && !cell?.disabled) onUpload(byKey.get(row.key).project, column.key);
  };

  const scopeLabel = scope === 'open' ? 'open' : '';
  // On a phone the grid scrolls sideways: fade its right edge while more stages are hidden there. The project
  // column is sticky on the left, so only the right side fades.
  const gridRef = useRef(null);
  const gridEdges = useScrollEdges(gridRef, {
    getNode: (wrapper) => wrapper?.querySelector('[role="grid"]')?.parentElement || null,
    watch: `${shownRows.length}:${columns.length}:${coverage.rows.length ? 1 : 0}`,
  });
  return (
    <section className="min-w-0 rounded-card border border-line bg-surface shadow-card" aria-labelledby="coverage-title" data-testid="documents-coverage">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line-2 px-4 py-3.5 sm:px-5">
        <div className="min-w-0">
          <h2 id="coverage-title" className="text-[15px] font-bold text-ink">Coverage by stage</h2>
          <p className="mt-0.5 text-[12.5px] text-ink-3">
            Latest documents per {scopeLabel ? 'open ' : ''}project and delivery stage — gaps show what has not been filed yet
            {scope === 'open' ? ' · on-hold projects last' : ''}
          </p>
        </div>
        <Segmented
          ariaLabel="Projects in coverage"
          value={scope}
          onChange={(next) => { setScope(next); setExpanded(false); }}
          options={[
            { key: 'open', label: 'Open projects', testId: 'coverage-scope-open' },
            { key: 'all', label: 'All', testId: 'coverage-scope-all' },
          ]}
        />
      </div>
      <div className="px-3 pb-2 pt-3 sm:px-4">
        {projectsError ? (
          <EmptyState layout="inline" tone="red" icon={FolderX} title="Couldn't load the projects" text={projectsError} />
        ) : (projectsLoading || !projects) && !projects ? (
          <div className="space-y-2 py-2" aria-label="Loading coverage">
            {[0, 1, 2].map((index) => <div key={index} className="ui-skeleton-shimmer h-8 rounded-lg bg-surface-2" />)}
          </div>
        ) : coverage.rows.length === 0 ? (
          <EmptyState layout="inline" icon={FolderX} title={scope === 'open' ? 'No open projects' : 'No projects yet'} text="Projects and their delivery stages appear here." />
        ) : (
          <div ref={gridRef} style={edgeFadeStyle({ end: gridEdges.end })} data-overflow={gridEdges.end ? 'true' : undefined} data-testid="coverage-scroller">
            <MatrixGrid
              ariaLabel="Documents filed per project and delivery stage"
              rowHeader="Project"
              rows={rows}
              columns={columns}
              getCell={getCell}
              onCellClick={onCellClick}
              cellWidth={76}
              testId="coverage"
            />
          </div>
        )}
      </div>
      {coverage.rows.length ? (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-line-2 px-4 py-3 text-[12px] text-ink-3 sm:px-5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid="coverage-legend">
            <span className="inline-flex items-center gap-1.5"><LegendPill tone="navy">2</LegendPill>documents filed</span>
            <span className="inline-flex items-center gap-1.5"><LegendPill tone="amber">Due</LegendPill>stage in progress, nothing filed</span>
            <span className="inline-flex items-center gap-1.5"><LegendPill tone="red">Missing</LegendPill>stage approved, no document</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[color:var(--status-gray)]" aria-hidden="true" />not started</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span data-testid="coverage-summary">
              {coverage.withoutDocuments === coverage.rows.length
                ? `None of the ${plural(coverage.rows.length, `${scopeLabel ? 'open ' : ''}project`)} has documents filed yet`
                : `${coverage.withoutDocuments} of ${plural(coverage.rows.length, `${scopeLabel ? 'open ' : ''}project`)} ${coverage.withoutDocuments === 1 ? 'has' : 'have'} no documents yet`}
            </span>
            {coverage.unlinked ? (
              <button type="button" onClick={onShowUnlinked} className="inline-flex items-center gap-1 font-semibold text-status-amber-ink hover:underline">
                <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
                {plural(coverage.unlinked, 'unlinked document')} not shown
              </button>
            ) : null}
            {coverage.rows.length > COLLAPSED_ROWS ? (
              <button
                type="button"
                onClick={() => setExpanded((value) => !value)}
                className="inline-flex items-center gap-1 font-semibold text-brand-ink hover:underline"
                aria-expanded={expanded}
                data-testid="coverage-toggle"
              >
                {expanded ? 'Show fewer' : `Show all ${coverage.rows.length}`}
                {expanded ? <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />}
              </button>
            ) : null}
            {!expanded && hidden > 0 ? <span className="sr-only">{hidden} more projects hidden</span> : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
